import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, FC, ReactNode, SetStateAction } from "react";
import clsx from "clsx";
import { MobileClose } from "../../../assets/svg/auth/MobileClose";
import { Close } from "../../../assets/svg/auth/Close";
import { useCloseOnClickOutside } from "../../../hooks/useCloseOnClickOutside";
import type { AuthPage } from "../../../types";
import { Login } from "../pages/Login";
import { Register } from "../pages/Register";
import { RegisterOtpInput } from "../pages/RegisterOtpInput";
import { PasswordReset } from "../pages/PasswordReset";
import { PasswordResetSuccess } from "../pages/PasswordResetSuccess";
import { RegisterSuccess } from "../pages/RegisterSuccess";
import { ForgotPassword } from "../pages/ForgotPassword";
import { PasswordResetOtpInput } from "../pages/PasswordResetOtpInput";

type AuthLayoutProps = {
  setIsAuthOpen: Dispatch<SetStateAction<boolean>>;
  currentPage: AuthPage;
  setCurrentPage: Dispatch<SetStateAction<AuthPage>>;
  loginInputValues: { email: string; password: string };
  setLoginInputValues: Dispatch<
    SetStateAction<{ email: string; password: string }>
  >;
  registerInputValues: { email: string; password: string };
  setRegisterInputValues: Dispatch<
    SetStateAction<{ email: string; password: string }>
  >;
  forgotPasswordInputValues: { email: string };
  setForgotPasswordInputValues: Dispatch<SetStateAction<{ email: string }>>;
};

export const AuthLayout: FC<AuthLayoutProps> = ({
  setIsAuthOpen,
  currentPage,
  setCurrentPage,
  loginInputValues,
  setLoginInputValues,
  registerInputValues,
  setRegisterInputValues,
  forgotPasswordInputValues,
  setForgotPasswordInputValues,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [, setIsWidePopup] = useState<boolean>(false);

  const closeAuth = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setIsAuthOpen(false);
    }, 340);
  }, [isClosing, setIsAuthOpen]);

  const setAuthOpenWithAnimation: Dispatch<SetStateAction<boolean>> =
    useCallback(
      (value) => {
        const shouldOpen = typeof value === "function" ? value(true) : value;
        if (shouldOpen) {
          setIsAuthOpen(true);
          return;
        }

        closeAuth();
      },
      [closeAuth, setIsAuthOpen],
    );

  useEffect(() => {
    overlayRef.current?.focus({ preventScroll: true });
  }, [currentPage]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const renderPage = (): ReactNode => {
    switch (currentPage) {
      case "LOGIN":
        return (
          <Login
            setCurrentPage={setCurrentPage}
            setIsAuthOpen={setAuthOpenWithAnimation}
            inputValues={loginInputValues}
            setInputValues={setLoginInputValues}
          />
        );
      case "REGISTER":
        return (
          <Register
            setCurrentPage={setCurrentPage}
            inputValues={registerInputValues}
            setInputValues={setRegisterInputValues}
            setIsWidePopup={setIsWidePopup}
          />
        );
      case "REGISTER_OTP_INPUT":
        return <RegisterOtpInput setCurrentPage={setCurrentPage} />;
      case "REGISTER_SUCCESS":
        return <RegisterSuccess setCurrentPage={setCurrentPage} />;
      case "PASSWORD_RESET_OTP_INPUT":
        return <PasswordResetOtpInput setCurrentPage={setCurrentPage} />;
      case "PASSWORD_RESET":
        return <PasswordReset setCurrentPage={setCurrentPage} />;
      case "PASSWORD_RESET_SUCCESS":
        return <PasswordResetSuccess setCurrentPage={setCurrentPage} />;
      case "FORGOT_PASSWORD":
        return (
          <ForgotPassword
            setCurrentPage={setCurrentPage}
            setInputValues={setForgotPasswordInputValues}
            inputValues={forgotPasswordInputValues}
          />
        );
      default:
        return null;
    }
  };

  useCloseOnClickOutside({
    popupRef: overlayRef,
    setIsPopupOpenAction: setAuthOpenWithAnimation,
  });

  return (
    <div
      className={
        "fixed inset-0 z-[60] flex min-h-[100dvh] w-full items-end bg-black--opaque backdrop-blur-[4px] sm:items-center sm:justify-center md:justify-end"
      }
    >
      <div
        ref={overlayRef}
        role={"dialog"}
        aria-modal={"true"}
        tabIndex={-1}
        className={clsx(
          "relative flex min-h-[100dvh] w-full flex-col overflow-y-auto bg-white sm:min-h-0 sm:w-auto md:h-full md:w-[630px]",
          "[&>div]:w-full [&>div]:sm:pt-[46px] [&>div]:md:pt-0",
          "sm:h-min",
          "px-[16px] py-[30px] sm:px-[30px] md:px-[122px]",
          "md:flex md:items-center md:justify-center",
          "rounded-t-20 sm:rounded-b-20 md:rounded-none",
          "auth-popup-panel",
          isClosing && "auth-popup-panel-closing",
        )}
      >
        {renderPage()}
        <button
          aria-label={"Close popup"}
          className={"absolute right-4 top-[-34px] sm:hidden"}
          onClick={closeAuth}
        >
          <MobileClose />
        </button>
        <button
          aria-label={"Close popup"}
          className={"absolute right-[30px] top-[30px] hidden sm:block"}
          onClick={closeAuth}
        >
          <Close />
        </button>
      </div>
    </div>
  );
};

export default AuthLayout;
